<template>
  <div class="onboarding-page mt-3" v-if="company && allowedSteps.length">
    <no-ssr>
      <component
        :is="focusedComponent"
        :steps="allowedSteps"
        :currentStep="step"
        :isJobAlert="isJobAlert"
        @goNext="$event => goNext($event)"
        @goBack="goBack"
        @toggleIsJobAlert="isJobAlert = !isJobAlert"
      />
    </no-ssr>
  </div>
</template>

<script>
import LoadingSpinner from '@src/components/misc/LoadingSpinner';
import { mapRoot } from '@src/mixins/root';

const loadingBlock = {
  functional: true,
  render: el => el(LoadingSpinner, { class: 'd-flex justify-content-center align-items-middle my-5' }),
};

export const onboardingSteps = [
  {
    name: 'video',
    displayName: 'Intro Video',
    stepper: false,
    component: () => ({
      component: import('@/views/onboarding/Video'),
      loading: loadingBlock,
    }),
  },
  {
    name: 'introduction',
    displayName: 'Introduction',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/Introduction'),
      loading: loadingBlock,
    }),
  },
  {
    name: 'registernetwork',
    displayName: 'Referrals',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/RegisterNetwork'),
      loading: loadingBlock,
    }),
  },
  {
    name: 'alerts',
    displayName: 'Alerts',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/Alerts'),
      loading: loadingBlock,
    }),
  },
  {
    name: 'slack',
    displayName: 'Slack',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/Slack'),
      loading: loadingBlock,
    }),
  },
  {
    name: 'skills',
    displayName: 'Skills',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/Skills'),
      loading: loadingBlock,
    }),
    types: [ 'STRING', 'ENUM', 'JOB_SKILLS', 'JOB_BUSINESS_AREA', 'MULTI_SELECT', 'LOCATION', 'JOB_DEVELOPMENT_SKILLS', 'JOB_TITLES', 'QUALIFICATIONS', 'DATE', 'JOB_HISTORY', 'LANGUAGES', 'TEXTAREA', 'CV_UPLOAD' ],
  },
  {
    name: 'career',
    displayName: 'Career aspirations',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/CareerAspirations'),
      loading: loadingBlock,
    }),
    types: [ 'STRING', 'ENUM', 'JOB_SKILLS', 'JOB_BUSINESS_AREA', 'MULTI_SELECT', 'LOCATION', 'JOB_DEVELOPMENT_SKILLS', 'JOB_TITLES', 'QUALIFICATIONS', 'DATE', 'JOB_HISTORY', 'LANGUAGES', 'TEXTAREA', 'CV_UPLOAD', 'RANGE', 'CHECKBOX' ],
  },
  {
    name: 'interest',
    displayName: 'Interests',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/Interest'),
      loading: loadingBlock,
    }),
    types: [ 'STRING', 'ENUM', 'JOB_SKILLS', 'JOB_BUSINESS_AREA', 'MULTI_SELECT', 'JOB_SENIORITY', 'DATE', 'JOB_TITLES', 'LOCATION', 'TEXTAREA' ],
  },
  {
    name: 'mentorship',
    displayName: 'Mentorship',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/Mentorship'),
      loading: loadingBlock,
    }),
    types: [ 'STRING', 'ENUM', 'JOB_SKILLS', 'JOB_BUSINESS_AREA', 'MULTI_SELECT', 'QUALIFICATIONS', 'DATE', 'JOB_HISTORY', 'LANGUAGES', 'RANGE', 'TEXTAREA', 'CHECKBOX' ],
  },
  // {
  //   name: 'jobs',
  //   displayName: 'Jobs',
  //   stepper: true,
  //   component: () => ({
  //     component: import('@/views/onboarding/Jobs'),
  //     loading: loadingBlock,
  //   }),
  //   types: [ 'STRING', 'JOB_TITLES', 'LOCATION', 'RANGE' ],
  // },
  // {
  //   name: 'development',
  //   displayName: 'Development',
  //   stepper: true,
  //   component: () => ({
  //     component: import('@/views/onboarding/Development'),
  //     loading: loadingBlock,
  //   }),
  //   types: [ 'STRING', 'ENUM', 'JOB_SKILLS', 'QUALIFICATIONS', 'DATE', 'JOB_HISTORY', 'LANGUAGES' ],
  // },
  {
    name: 'mentorshiprole',
    displayName: '(CXR) Mentorship Role',
    stepper: false,
    component: () => ({
      component: import('@/views/onboarding/MentorshipRole'),
      loading: loadingBlock,
    }),
  },
  {
    name: 'mentorpreferences',
    displayName: '(CXR) Your Mentor',
    stepper: true,
    component: () => ({
      component: import('@/views/onboarding/MentorPreferences'),
      loading: loadingBlock,
    }),
    types: [ 'STRING', 'ENUM', 'JOB_SKILLS', 'JOB_BUSINESS_AREA', 'MULTI_SELECT', 'JOB_SENIORITY', 'JOB_TITLES', 'LOCATION' ],
  },
];

export default {
  name: 'Onboarding',
  props: {
    stepName: {
      type: String,
      default: () => null,
    },
    nextRoute: {
      type: Object,
      default: () => null,
    },
  },
  data: () => ({
    isJobAlert: true,
    mentorStatus: 'BOTH',
  }),
  mounted() {
    if (this.allowedSteps.length === 0) {
      this.goNext();
    }
  },
  computed: {
    ...mapRoot([ 'company', 'user', 'isNewDashboard' ]),
    allowedSteps() {
      const { siteBranding: { onboarding } = {}, sitePreferences: { isInternalMobilityEnabled, isMentorshipFeatureEnabled } = {} } = this.company || {};

      let steps = (Array.isArray(onboarding) ? onboarding : []).reduce((out, o) => {
        const step = o && o.name ? onboardingSteps.find(s => s && s.name === o.name) : null;
        this.$log.debug('FROM ALLOWED STEPS: ', this.mentorStatus);
        if (this.user && this.mentorStatus === 'ACTIVE' && step.name === 'mentorpreferences') {
          return out;
        }
        if (this.user && !this.mentorStatus && step.name === 'mentorship') {
          return out;
        }
        if (step) {
          out.push(step);
        }
        return out;
      }, []);

      // make sure that misconfigured company settings cannot crash onboarding
      if (isInternalMobilityEnabled || isMentorshipFeatureEnabled) {
        steps = steps.filter(step => this.isJobAlert || step.name !== 'interest');
      } else {
        steps = steps.filter(({ name }) => [ 'video', 'registernetwork', 'slack', 'alerts' ].includes(name));
      }

      // make sure that stepper is not displayed if there are too few steps
      if (steps.filter(({ name }) => ![ 'video', 'slack' ].includes(name)).length < 3) {
        steps = steps.map(step => {
          step.stepper = false;
          return step;
        });
      }

      return steps;
    },
    step() {
      if (this.stepName) {
        return this.allowedSteps.find(step => step.name === this.stepName) || this.allowedSteps[0];
      } else {
        return this.allowedSteps[0];
      }
    },
    nextStep() {
      const stepNames = this.allowedSteps.map(step => step.name);
      return this.step ? this.allowedSteps[stepNames.indexOf(this.step.name) + 1] : null;
    },
    prevStep() {
      const stepNames = this.allowedSteps.map(step => step.name);
      return this.step ? this.allowedSteps[stepNames.indexOf(this.step.name) - 1] : null;
    },
    focusedComponent() {
      const { component } = this.step || {};
      return component || null;
    },
  },
  methods: {
    goNext(payload) {
      if (payload) {
        this.mentorStatus = payload.mentorStatus;
      }
      if (this.nextStep) {
        const { name, params } = this.$route;
        const { name: stepName } = this.nextStep;
        this.$router.push({ name, params: { ...params, stepName } });
      } else {
        this.$router.push(this.nextRoute || {
          name: this.$root.isOnlyMentorship ? (this.mentorStatus === 'ACTIVE' ? 'UserMentorship' : 'MentorsIndex') : 'Dashboard',
          query: { welcome: 1 },
        });
      }
    },
    goBack({ sync = null } = {}) {
      if (this.prevStep) {
        const { name, params } = this.$route;
        const { name: stepName } = this.prevStep;
        this.$router.push({ name, params: { ...params, stepName } });
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/onboarding/index.vue